import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/InstallApp";
import { ChangePasswordButton } from "@/components/ChangePassword";
import { EditProfileButton } from "@/components/EditProfile";

const NavItem = ({ item, active, onClick }) => (
  <button
    data-testid={`nav-tab-${item.key}`}
    onClick={onClick}
    className={`flex shrink-0 items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-200 ${
      active ? "bg-blue-600 text-white shadow-lg shadow-blue-900/40" : "text-blue-100/80 hover:bg-white/5 hover:text-white"
    }`}
  >
    <item.icon className="h-4 w-4" />
    {item.label}
    {item.badge > 0 && (
      <span className="ml-auto rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-slate-900">{item.badge}</span>
    )}
  </button>
);

export const DashboardShell = ({ title, subtitle, roleLabel, items, active, onChange, actions, children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const signOut = () => {
    logout();
    nav("/login");
  };
  return (
    <div className="min-h-screen bg-[#F4F8FD] lg:flex">
      <aside className="grain relative bg-[#0B192C] lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:flex-col">
        <div className="flex items-center justify-between px-5 py-4 lg:py-6">
          <Logo dark to="/" />
          <div className="flex items-center gap-4 lg:hidden">
            <EditProfileButton iconOnly testId="edit-profile-button-mobile" className="text-blue-200" />
            <ChangePasswordButton iconOnly testId="change-password-button-mobile" className="text-blue-200" />
            <button data-testid="logout-button-mobile" onClick={signOut} className="text-blue-200">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="hidden px-6 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400 lg:block">{roleLabel}</p>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible">
          {items.map((it) => (
            <NavItem key={it.key} item={it} active={active === it.key} onClick={() => onChange(it.key)} />
          ))}
        </nav>
        <div className="mt-auto hidden border-t border-white/10 p-4 lg:block">
          <p className="truncate text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</p>
          <p className="truncate text-xs text-blue-200">{user?.email}</p>
          <InstallAppButton className="mt-3 text-xs font-semibold text-amber-400 hover:text-amber-300" />
          <EditProfileButton className="mt-3 flex items-center gap-2 text-xs font-semibold text-blue-200 transition-colors hover:text-amber-400" />
          <ChangePasswordButton className="mt-3 flex items-center gap-2 text-xs font-semibold text-blue-200 transition-colors hover:text-amber-400" />
          <button
            data-testid="logout-button"
            onClick={signOut}
            className="mt-3 flex items-center gap-2 text-xs font-semibold text-blue-200 transition-colors hover:text-amber-400"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-8 lg:px-10 lg:py-10">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600">{roleLabel}</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="dashboard-title">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
};
